import { APPLICATION_STAGES, statusToStage } from '@constants/applicationStatus';

/**
 * Styled Excel export for the job-applications table.
 *
 * Replaces a hand-rolled CSV. CSV carried no types, so "Applied On" arrived as text that Excel
 * would not sort or filter as a date, and every row looked identical — the reader had to read the
 * Stage column to find the ones that matter.
 *
 * Colours are the Control Center's own stage palette (design-system.scss), resolved to hex here
 * because Excel needs ARGB and cannot read CSS custom properties. Keep in sync if the palette moves.
 */

/** Stage token -> the same background/text pair the UI chips use. */
const STAGE_HEX: Record<string, { bg: string; text: string }> = {
  qualified: { bg: 'E0F2FE', text: '0369A1' }, // Qualified, Submitted
  lead: { bg: 'EEF2FF', text: '4338CA' }, // Interested, Screening
  proposal: { bg: 'FEF3C7', text: 'B45309' }, // Interview
  negotiation: { bg: 'FFEDD5', text: 'C2410C' }, // Offer
  won: { bg: 'D1FAE5', text: '065F46' }, // Joined
  lost: { bg: 'FEE2E2', text: '991B1B' }, // Closed
  muted: { bg: 'F5F5F5', text: '757575' }, // Sourcing, Contacted
};

const HEADER_BG = '0369A1'; // --color-primary, the logo's deep ocean blue
const BORDER = 'C9D4E3'; // --color-border

/** Maps a status to its stage's colours, via the same taxonomy the UI uses. */
function colorsForStatus(status: string): { bg: string; text: string } | null {
  const stage = statusToStage(status);
  if (!stage) return null;
  const def = APPLICATION_STAGES.find((s) => s.stage === stage);
  return def ? (STAGE_HEX[def.token] ?? null) : null;
}

export interface ApplicationExportRow {
  name: string;
  designation: string;
  companyName: string;
  status: string;
  stage: string;
  /** The detail, or for a Closed record its reason. */
  detail?: string;
  /** The candidate's recruiter first, else the older assignment. */
  hrIds: string[];
  nextAction?: string;
  nextActionAt?: string;
  createdAt?: string;
  /** 0 to 100; null or absent when not scored, which exports as an empty cell rather than 0. */
  match?: number | null;
}

/**
 * Builds and downloads the workbook.
 *
 * @param rows      every row matching the current filters (not just the visible page)
 * @param hrById    HR id -> display name, so the HR column reads as names not ids
 * @param filterNote human-readable summary of the filters in force, written above the table so an
 *                   exported file is self-describing rather than an anonymous grid of rows
 */
export async function exportApplicationsExcel(
  rows: ApplicationExportRow[],
  hrById: Record<string, string>,
  filterNote?: string,
): Promise<void> {
  // Loaded on demand: exceljs is ~940kB, and only a Super Admin clicking Export ever needs it.
  // A static import put the whole library in the main bundle for every page of the CC.
  const ExcelJS = (await import('exceljs')).default;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Mintly Control Center';
  workbook.created = new Date();

  const sheet = workbook.addWorksheet('Job Applications', {
    views: [{ state: 'frozen', ySplit: filterNote ? 3 : 2 }], // keep headers visible while scrolling
  });

  // Title block: says what this is and when it was taken, so a file forwarded by email still
  // explains itself.
  const title = sheet.addRow(['Mintly: Job Applications']);
  title.font = { bold: true, size: 14, color: { argb: 'FF212121' } };
  sheet.mergeCells(title.number, 1, title.number, 10);

  if (filterNote) {
    const note = sheet.addRow([filterNote]);
    note.font = { italic: true, size: 10, color: { argb: 'FF757575' } };
    sheet.mergeCells(note.number, 1, note.number, 10);
  }

  const columns = [
    { header: 'Applied On', key: 'appliedOn', width: 20 },
    { header: 'Candidate', key: 'candidate', width: 26 },
    { header: 'Client', key: 'client', width: 26 },
    { header: 'Position', key: 'position', width: 30 },
    { header: 'Stage', key: 'stage', width: 16 },
    { header: 'Detail or reason', key: 'detail', width: 24 },
    { header: 'Recruiter', key: 'hr', width: 24 },
    { header: 'Next action', key: 'nextAction', width: 32 },
    { header: 'Due', key: 'due', width: 14 },
    // Last, so the cell indices the stage colouring and date formats address stay where they were.
    { header: 'AI match %', key: 'match', width: 12 },
  ];

  const headerRow = sheet.addRow(columns.map((c) => c.header));
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${HEADER_BG}` } };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.border = { bottom: { style: 'thin', color: { argb: `FF${BORDER}` } } };
  });
  headerRow.height = 22;
  columns.forEach((c, i) => {
    sheet.getColumn(i + 1).width = c.width;
  });

  rows.forEach((r) => {
    const appliedOn = r.createdAt ? new Date(r.createdAt) : null;
    const row = sheet.addRow([
      // A real Date, not a string: Excel can then sort, filter and group by it.
      appliedOn && !isNaN(appliedOn.getTime()) ? appliedOn : '',
      r.name || '-',
      r.companyName || '-',
      r.designation || '-',
      r.stage || '-',
      r.detail || '-',
      r.hrIds?.length ? r.hrIds.map((id) => hrById[id] || id).join(', ') : '-',
      r.nextAction || '-',
      r.nextActionAt && !isNaN(new Date(r.nextActionAt).getTime()) ? new Date(r.nextActionAt) : '',
      // A number, so the column sorts and filters; blank when unscored.
      r.match != null ? Math.round(r.match) : '',
    ]);

    row.getCell(1).numFmt = 'dd mmm yyyy, hh:mm';
    row.getCell(9).numFmt = 'dd mmm yyyy';
    row.alignment = { vertical: 'middle' };

    // Colour Stage and its detail to match the UI chips, so the eye lands on the same things.
    const colors = colorsForStatus(r.status);
    if (colors) {
      [5, 6].forEach((col) => {
        const cell = row.getCell(col);
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${colors.bg}` } };
        cell.font = { color: { argb: `FF${colors.text}` }, bold: col === 5 };
      });
    }

    row.eachCell((cell) => {
      cell.border = { bottom: { style: 'hair', color: { argb: `FF${BORDER}` } } };
    });
  });

  // Autofilter over the header + data, so the file is usable without any setup.
  sheet.autoFilter = {
    from: { row: headerRow.number, column: 1 },
    to: { row: headerRow.number + rows.length, column: columns.length },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `job-applications-${new Date().toISOString().slice(0, 10)}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
